import { useState, useEffect, useRef, useCallback } from "react";
import { getHelpSlugsForRoute } from "./help-context";

interface ArticleMeta {
	slug: string;
	title: string;
	description: string;
	app: string;
	category: string;
	order: number;
	updatedAt: string;
}

interface Article extends ArticleMeta {
	markdown: string;
	url: string;
}

interface ArticleIndex {
	articles: ArticleMeta[];
	navigation: Record<string, { group: string; pages: string[] }[]>;
}

export interface RouteArticles {
	recommended: ArticleMeta[];
	other: ArticleMeta[];
}

interface UseHelpContentReturn {
	index: ArticleIndex | null;
	loading: boolean;
	getArticle: (slug: string) => Promise<Article | null>;
	getArticlesForRoute: (route: string, app: string) => RouteArticles;
	searchArticles: (query: string, app: string) => ArticleMeta[];
}

const HELPCENTER_URL =
	import.meta.env.VITE_HELPCENTER_URL || "https://hilfe.dentdesk.ch";

const indexCache: { data: ArticleIndex | null } = { data: null };
const articleCache = new Map<string, Article>();

export function useHelpContent(): UseHelpContentReturn {
	const [index, setIndex] = useState<ArticleIndex | null>(indexCache.data);
	const [loading, setLoading] = useState(!indexCache.data);
	const fetchedRef = useRef(false);

	useEffect(() => {
		if (indexCache.data || fetchedRef.current) return;
		fetchedRef.current = true;

		fetch(`${HELPCENTER_URL}/api/index.json`)
			.then((r) => r.json())
			.then((data) => {
				indexCache.data = data;
				setIndex(data);
				setLoading(false);
			})
			.catch(() => setLoading(false));
	}, []);

	const getArticle = useCallback(async (slug: string): Promise<Article | null> => {
		if (articleCache.has(slug)) return articleCache.get(slug)!;
		try {
			const res = await fetch(`${HELPCENTER_URL}/api/articles/${slug}.json`);
			if (!res.ok) return null;
			const data = await res.json();
			articleCache.set(slug, data);
			return data;
		} catch {
			return null;
		}
	}, []);

	const getArticlesForRoute = useCallback(
		(route: string, app: string): RouteArticles => {
			if (!index) return { recommended: [], other: [] };
			const appArticles = index.articles.filter(
				(a) => a.app === app || a.slug.startsWith(`${app}/`)
			);
			const recommendedSlugs = getHelpSlugsForRoute(route, app);
			if (!recommendedSlugs || recommendedSlugs.length === 0) {
				return { recommended: [], other: appArticles };
			}
			const slugSet = new Set(recommendedSlugs);
			const recommended: ArticleMeta[] = [];
			const other: ArticleMeta[] = [];
			for (const article of appArticles) {
				if (slugSet.has(article.slug)) {
					recommended.push(article);
				} else {
					other.push(article);
				}
			}
			return { recommended, other };
		},
		[index]
	);

	const searchArticles = useCallback(
		(query: string, app: string): ArticleMeta[] => {
			if (!index || !query.trim()) return [];
			const q = query.toLowerCase();
			return index.articles.filter(
				(a) =>
					(a.app === app || a.slug.startsWith(`${app}/`)) &&
					(a.title.toLowerCase().includes(q) ||
						a.description?.toLowerCase().includes(q))
			);
		},
		[index]
	);

	return { index, loading, getArticle, getArticlesForRoute, searchArticles };
}
